import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Loader2, Sparkles, X } from 'lucide-react';
import { api } from '@/lib/api';
import API_URL from '@/lib/apiConfig';
import {
  translateN8nSuggestions,
  runProviderTemplatePreflight,
  saveAppDirectCookie,
  PreflightPanel,
} from '@/components/match/ProviderTemplatesTab';
import { buildRecipeSummaryLines } from '@/lib/match/recipeHelpers';
import { labelForTargetPath } from '@/lib/match/contractH';

const BASE = `${API_URL}/api/match`;

// ---------------------------------------------------------------------------
// Stage machine
//   offer      — "This report isn't recognized. Want Magic Mapping?"
//   analyzing  — spinner while /suggest-mappings runs
//   review     — show suggested mappings + name/product inputs
//   created    — template created; offer "Import & Match" or "Not now"
// ---------------------------------------------------------------------------

const STAGES = { offer: 'offer', analyzing: 'analyzing', review: 'review', created: 'created' };

// Derive a sensible template-name prefill from the detected provider or filename.
function deriveTemplateName(detectedProvider, filename) {
  if (detectedProvider?.trim()) return detectedProvider.trim();
  if (!filename) return '';
  // Strip extension and common suffixes
  return filename
    .replace(/\.[^.]+$/, '')
    .replace(/[-_](report|export|data|upload|csv)$/i, '')
    .replace(/[-_]/g, ' ')
    .trim();
}

// Human-readable label for a target_path. Derived from contractH.js — the same
// source the mapping editor and the row detail drawer read, so one field has one
// name everywhere in MATCH.
//
// This was a hardcoded KNOWN map until 2026-07-29. It listed six paths Contract H
// never had (mrc, provider_sales_order_id, appdirect_order_id,
// contract_term_months, contract_start_date, contract_end_date) because it was
// copied from the suggest-mappings prompt, which had invented them. A local copy
// of a field list is the bug, not the fix.
const friendlyLabel = labelForTargetPath;

/**
 * MagicMapFlowModal
 *
 * Props:
 *   isOpen           — boolean
 *   reportUploadInfo — the upload response object from UploadField
 *                      { url, filename, original_filename, detected_provider, ... }
 *   detectedProvider — string (may be empty)
 *   asErrorText      — the shared error-normalization helper from SessionsTab
 *   onCreateAndImport(templateId, provider) — called when user clicks "Import & Match"
 *   onClose          — called when the modal should close without continuing
 *
 * Stage/suggestion state is NOT reset on close — the parent must key this
 * component by the uploaded report (key={uploads.provider_report?.url}) so a
 * new report remounts it fresh and any in-flight response for the old report
 * lands on the discarded instance. Same-report reopens intentionally keep
 * state ("Not now" then re-submit resumes at the created stage).
 */
export default function MagicMapFlowModal({
  isOpen,
  reportUploadInfo,
  detectedProvider,
  asErrorText,
  onCreateAndImport,
  onClose,
  // Optional override for the offer-stage explanation. Default copy says "no
  // template matches" — wrong when a template DID match but cannot parse the
  // report (recipe mismatch); the parent passes the accurate reason instead.
  offerReason,
}) {
  const [stage,          setStage]          = useState(STAGES.offer);
  const [suggestions,    setSuggestions]    = useState([]);
  const [parseRecipe,    setParseRecipe]    = useState(null);
  const [templateName,   setTemplateName]   = useState('');
  const [defaultProduct, setDefaultProduct] = useState('');
  const [createdId,      setCreatedId]      = useState(null);
  const [createdName,    setCreatedName]    = useState('');
  const [error,          setError]          = useState(null);
  const [saving,         setSaving]         = useState(false);

  // Preflight — auto-run the moment a template is created (see handler below).
  // Not reset on close/reopen for the same report, same rule as the rest of
  // this component's state (see file header): re-showing the created stage
  // should re-show the last preflight result, not blank it.
  const [preflight,      setPreflight]      = useState({ status: 'idle', checks: [], message: null });
  const [cookiePaste,    setCookiePaste]    = useState('');
  const [cookieSaving,   setCookieSaving]   = useState(false);
  const [cookieError,    setCookieError]    = useState(null);

  if (!isOpen) return null;

  // ---- handlers ----

  async function handleMagicMap() {
    setError(null);
    setStage(STAGES.analyzing);
    try {
      const res = await api.post(`${BASE}/provider-templates/suggest-mappings`, {
        provider_report_url: reportUploadInfo?.url,
      });
      const data = await res.json();
      if (!res.ok) {
        setError(asErrorText(data?.detail, 'Suggest-mappings request failed.'));
        setStage(STAGES.offer);
        return;
      }
      const rawList = Array.isArray(data)
        ? data
        : data?.column_mappings ?? data?.suggestions ?? [];
      const translated = translateN8nSuggestions(rawList);
      if (translated.length === 0) {
        setError('Magic Mapping returned no suggestions for this report.');
        setStage(STAGES.offer);
        return;
      }
      setSuggestions(translated);
      // Capture parse_recipe if present (raw grouped XLSX reports only).
      // Absent on flat CSV reports — that case must look unchanged.
      setParseRecipe(data?.parse_recipe ?? null);
      setTemplateName(deriveTemplateName(detectedProvider, reportUploadInfo?.original_filename));
      setStage(STAGES.review);
    } catch (exc) {
      setError(`Magic Mapping failed: ${exc?.message || exc}`);
      setStage(STAGES.offer);
    }
  }

  async function handleCreateTemplate() {
    if (!templateName.trim()) {
      setError('Provider / Template name is required.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const body = {
        name: templateName.trim(),
        default_product: defaultProduct.trim() || null,
        column_mappings: suggestions.map((m) => ({
          source_column: m.source_column,
          target_path:   m.target_path,
          transforms:    (m.transforms || []).filter((t) => t && t !== 'none'),
          required:      Boolean(m.required),
        })),
        // Pass the recipe VERBATIM — do not transform. Absent on flat CSV reports.
        ...(parseRecipe != null ? { parse_recipe: parseRecipe } : {}),
      };
      const res  = await api.post(`${BASE}/provider-templates`, body);
      const data = await res.json();
      if (!res.ok) {
        setError(asErrorText(data?.detail, 'Failed to create template.'));
        return;
      }
      setCreatedId(data.template_id);
      setCreatedName(templateName.trim());
      setStage(STAGES.created);
      // Fire-and-forget: the operator shouldn't have to ask for this, and it
      // must not delay the stage transition or block "Import & Match" below.
      runPreflight(data.template_id);
    } catch (exc) {
      setError(`Template creation failed: ${exc?.message || exc}`);
    } finally {
      setSaving(false);
    }
  }

  async function runPreflight(templateId) {
    setPreflight({ status: 'running', checks: [], message: null });
    setCookieError(null);
    setPreflight(await runProviderTemplatePreflight(templateId));
  }

  async function handleCookieSave() {
    if (!cookiePaste.trim()) { setCookieError('Paste your AppDirect session first.'); return; }
    setCookieSaving(true);
    setCookieError(null);
    const result = await saveAppDirectCookie(cookiePaste);
    setCookieSaving(false);
    if (!result.ok) { setCookieError(result.message); return; }
    setCookiePaste('');
    runPreflight(createdId);
  }

  function handleImportAndMatch() {
    onCreateAndImport(createdId, createdName);
  }

  // ---- render helpers ----

  const ErrorBanner = ({ msg }) =>
    msg ? (
      <div
        className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-boss-status-danger-bg border border-[var(--boss-danger-border)] text-boss-status-danger text-sm"
        role="alert"
        data-testid="magic-map-flow-error"
      >
        <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
        <span>{msg}</span>
      </div>
    ) : null;

  // ---- modal shell ----

  // Portal to document.body: `position: fixed` only anchors to the viewport when
  // no ancestor is its containing block. Any ancestor with transform, filter,
  // backdrop-filter, perspective, will-change or contain becomes one, and inset-0
  // then resolves against THAT element's padding box — measured on dev as top=16
  // against innerHeight=945, leaving the top 16px of the page undimmed.
  // overflow-hidden because a viewport-sized fixed element with overflow set
  // becomes its own scroll container, whose scrollbar shrinks the click-catcher.
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center p-4 pt-16 bg-black/60 overflow-hidden"
      role="dialog"
      aria-modal="true"
      aria-labelledby="magic-map-flow-title"
    >
      {/* Backdrop — close only on offer/created stages (not mid-flow) */}
      <div
        className="absolute inset-0"
        aria-hidden="true"
        onClick={stage === STAGES.offer || stage === STAGES.created ? onClose : undefined}
      />

      <div className="relative flex flex-col max-h-[calc(100vh-6rem)] bg-boss-surface border border-boss-border rounded-xl shadow-2xl w-full max-w-lg">
        {/* Header — shrink-0 so it keeps its height when the body is squeezed */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-boss-border shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-boss-accent" />
            <h2
              id="magic-map-flow-title"
              className="text-sm font-semibold text-boss-text"
            >
              {stage === STAGES.offer     && 'Report Not Recognized'}
              {stage === STAGES.analyzing && 'Magic Mapping'}
              {stage === STAGES.review    && 'Review Suggested Mappings'}
              {stage === STAGES.created   && 'Template Created'}
            </h2>
          </div>
          {(stage === STAGES.offer || stage === STAGES.created) && (
            <button
              type="button"
              onClick={onClose}
              className="text-boss-text-muted hover:text-boss-text-secondary transition"
              aria-label="Close"
              data-testid="magic-map-flow-close"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Body */}
        {/* Body — the single scroll region. min-h-0 is required: flex children
            default to min-height:auto and refuse to shrink below content size,
            and a flex child that cannot shrink never scrolls. */}
        <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1 min-h-0">

          {/* ---- STAGE: offer ---- */}
          {stage === STAGES.offer && (
            <>
              {offerReason ? (
                <p className="text-sm text-boss-text-secondary">{offerReason}</p>
              ) : (
                <p className="text-sm text-boss-text-secondary">
                  No column mapping template matches this report
                  {detectedProvider ? (
                    <> (detected provider: <strong className="text-boss-text">{detectedProvider}</strong>)</>
                  ) : ' and the provider could not be detected from the filename'}.
                </p>
              )}
              {reportUploadInfo?.converted_from_xlsx && (
                <p
                  className="text-xs text-boss-text-muted"
                  data-testid="report-structure-xlsx-note"
                >
                  Converted from Excel
                  {reportUploadInfo.sheet_name ? ` (sheet: ${reportUploadInfo.sheet_name})` : ''}
                </p>
              )}
              <p className="text-sm text-boss-text-secondary">
                Want Magic Mapping to analyze the report columns and build a template automatically?
              </p>
              <ErrorBanner msg={error} />
              <div className="flex items-center gap-3 pt-1">
                <button
                  type="button"
                  className="btn-primary flex items-center gap-2 text-sm px-4 py-2"
                  onClick={handleMagicMap}
                  data-testid="magic-map-flow-start-btn"
                >
                  <Sparkles size={14} />
                  Magic Map It
                </button>
                <button
                  type="button"
                  className="text-sm text-boss-text-secondary hover:text-boss-text transition"
                  onClick={onClose}
                  data-testid="magic-map-flow-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </>
          )}

          {/* ---- STAGE: analyzing ---- */}
          {stage === STAGES.analyzing && (
            <div className="flex flex-col items-center gap-4 py-6">
              <Loader2 size={28} className="text-boss-accent animate-spin" />
              <p className="text-sm text-boss-text-secondary">
                Analyzing report columns…
              </p>
              <p className="text-xs text-boss-text-muted">
                This usually takes 10–15 seconds.
              </p>
            </div>
          )}

          {/* ---- STAGE: review ---- */}
          {stage === STAGES.review && (
            <>
              {/* Report structure detected card — only when parse_recipe is present */}
              {parseRecipe && (() => {
                const { lines, notes } = buildRecipeSummaryLines(parseRecipe);
                if (lines.length === 0 && !notes) return null;
                return (
                  <div
                    className="rounded-lg border border-boss-border bg-boss-accent-muted px-4 py-3 space-y-1.5"
                    data-testid="report-structure-card"
                  >
                    <p className="text-xs font-semibold text-boss-accent">
                      Report structure detected
                    </p>
                    <ul className="space-y-0.5">
                      {lines.map((line, i) => (
                        <li
                          key={i}
                          className="text-xs text-boss-text-secondary"
                          data-testid={`report-structure-line-${i}`}
                        >
                          {line}
                        </li>
                      ))}
                    </ul>
                    {notes && (
                      <p className="text-xs text-boss-text-muted italic" data-testid="report-structure-notes">
                        {notes}
                      </p>
                    )}
                  </div>
                );
              })()}

              <div className="space-y-3">
                <div>
                  <label
                    className="block text-xs font-medium text-boss-text-secondary mb-1"
                    htmlFor="magic-map-template-name"
                  >
                    Provider / Template Name <span className="text-boss-status-danger">*</span>
                  </label>
                  <input
                    id="magic-map-template-name"
                    className="input-field w-full"
                    placeholder="e.g. RingCentral"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    data-testid="magic-map-flow-template-name"
                  />
                  <p className="mt-1 text-xs text-boss-text-muted">
                    This becomes both the template name and the session provider — keeping them
                    identical means this report will be auto-recognized next time.
                  </p>
                </div>
                <div>
                  <label
                    className="block text-xs font-medium text-boss-text-secondary mb-1"
                    htmlFor="magic-map-default-product"
                  >
                    Default Product{' '}
                    <span className="text-boss-text-muted font-normal">(optional)</span>
                  </label>
                  <input
                    id="magic-map-default-product"
                    className="input-field w-full"
                    placeholder="e.g. UCaaS"
                    value={defaultProduct}
                    onChange={(e) => setDefaultProduct(e.target.value)}
                    data-testid="magic-map-flow-default-product"
                  />
                </div>
              </div>

              {/* Suggested mappings list — read-only */}
              <div>
                <p className="text-xs font-medium text-boss-text-secondary mb-2">
                  Suggested column mappings ({suggestions.length})
                </p>
                <div className="max-h-56 overflow-y-auto space-y-1 pr-1 rounded-lg border border-boss-border bg-boss-elevated p-2">
                  {suggestions.map((s, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 text-xs py-1 px-1"
                      data-testid={`magic-map-flow-mapping-row-${i}`}
                    >
                      <span className="text-boss-text font-medium flex-1 truncate">
                        {s.source_column || '—'}
                      </span>
                      <span className="text-boss-text-muted">→</span>
                      <span className="text-boss-accent flex-1 truncate">
                        {friendlyLabel(s.target_path)}
                      </span>
                      {s.required && (
                        <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium bg-boss-status-warn-bg text-boss-status-warn border border-[var(--boss-warning-border)]">
                          required
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <ErrorBanner msg={error} />

              <div className="flex items-center gap-3 pt-1">
                <button
                  type="button"
                  className="btn-primary flex items-center gap-2 text-sm px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleCreateTemplate}
                  disabled={saving}
                  data-testid="magic-map-flow-create-btn"
                >
                  {saving ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <CheckCircle2 size={14} />
                  )}
                  {saving ? 'Creating…' : 'Create Template'}
                </button>
                <button
                  type="button"
                  className="text-sm text-boss-text-secondary hover:text-boss-text transition"
                  onClick={() => { setError(null); setStage(STAGES.offer); }}
                  disabled={saving}
                  data-testid="magic-map-flow-back-btn"
                >
                  Back
                </button>
              </div>
            </>
          )}

          {/* ---- STAGE: created ---- */}
          {stage === STAGES.created && (
            <>
              <div className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-boss-status-success flex-shrink-0 mt-0.5" />
                <p className="text-sm text-boss-text-secondary">
                  Template{' '}
                  <strong className="text-boss-text">&lsquo;{createdName}&rsquo;</strong>{' '}
                  created. Continue to MATCH this report now?
                </p>
              </div>

              <PreflightPanel
                preflight={preflight}
                cookiePaste={cookiePaste}
                onCookiePasteChange={setCookiePaste}
                cookieSaving={cookieSaving}
                cookieError={cookieError}
                onCookieSave={handleCookieSave}
                onRetry={() => runPreflight(createdId)}
              />

              {/* Preflight is a warning surface, not a gate — these stay
                  enabled regardless of preflight status. Booking is already
                  gated elsewhere in the MATCH pipeline. */}
              <div className="flex items-center gap-3 pt-1">
                <button
                  type="button"
                  className="btn-primary flex items-center gap-2 text-sm px-4 py-2"
                  onClick={handleImportAndMatch}
                  data-testid="magic-map-flow-import-match-btn"
                >
                  <CheckCircle2 size={14} />
                  Import &amp; Match
                </button>
                <button
                  type="button"
                  className="text-sm text-boss-text-secondary hover:text-boss-text transition"
                  onClick={onClose}
                  data-testid="magic-map-flow-not-now-btn"
                >
                  Not now
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
